"use client";

import { useState, useEffect, useMemo } from "react";
import { humanizeStatus } from "@/app/_module/utils/humanizeStatus";
import { Users, DollarSign, TrendingUp, AlertTriangle, Check, X, Clock, MoreVertical, ArrowUpCircle, ArrowDownCircle, Tag, Loader2 } from "lucide-react";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { useToast } from "@/app/_module/hooks/use-toast";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/app/_module/components/ui/dropdown-menu";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import { Switch } from "@/app/_module/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/_module/components/ui/table";

import {
  getAgencySeats,
  togglePaymentSource,
  changeAgentPlan,
  getProrationPreview,
  validateAgencyPromoCode,
  applyAgencyCoupon,
  getHierarchyAgents,
  getEnterpriseSeatSummary,
  toggleEnterpriseCoverage,
  type PromoCodeValidationResult,
  type HierarchyAgent,
  type EnterpriseSeatSummary,
} from "@/app/_module/services/api/agency-billing.api";
import { Input } from "@/app/_module/components/ui/input";
import { Progress } from "@/app/_module/components/ui/progress";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import {
  PaymentSource,
  PlanType,
  SeatStatus,
} from "@/app/_module/types/agency-billing.types";

import type {
  AgencyBillingSeat,
  AgencyBillingSummary,
  ProrationPreview,
  GracePeriodInfo,
  AgentTableRow,
} from "@/app/_module/types/agency-billing.types";
import { isDemoMode } from "@/app/_module/utils/demoMode";

interface ConfirmationDialogState {
  open: boolean;
  type: "toggle_payment" | "change_plan" | null;
  seat: AgencyBillingSeat | null;
  newValue?: string;
  proration?: ProrationPreview;
}

interface AgencyBillingDashboardProps {
  isEnterpriseOwner?: boolean;
}

export const AgencyBillingDashboard = function ({ isEnterpriseOwner = false }: AgencyBillingDashboardProps) {
  const { user } = useUserContext();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [seats, setSeats] = useState<AgencyBillingSeat[]>([]);
  const [summary, setSummary] = useState<AgencyBillingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmationDialogState>({
    open: false,
    type: null,
    seat: null,
  });
  const [coverageLoading, setCoverageLoading] = useState<string | null>(null);

  // Promo code state
  const [promoCode, setPromoCode] = useState("");
  const [promoCodeLoading, setPromoCodeLoading] = useState(false);
  const [promoCodeResult, setPromoCodeResult] = useState<PromoCodeValidationResult | null>(null);

  const agencyId = user?.currentAgency?.agency?.id;

  // Enterprise coverage data (only fetched for enterprise owners)
  const { data: hierarchyAgents, refetch: refetchHierarchy } = useQuery({
    queryKey: ['enterprise-hierarchy-agents'],
    queryFn: getHierarchyAgents,
    enabled: isEnterpriseOwner,
    staleTime: 1000 * 60 * 2,
  });

  const { data: seatSummary, refetch: refetchSeatSummary } = useQuery({
    queryKey: ['enterprise-seat-summary'],
    queryFn: getEnterpriseSeatSummary,
    enabled: isEnterpriseOwner,
    staleTime: 1000 * 60 * 2,
  });

  // Handle coverage toggle for enterprise owner
  const handleToggleCoverage = async (agent: HierarchyAgent, covered: boolean, planType: 'lite' | 'basic' | 'full_fledged') => {
    try {
      setCoverageLoading(agent.userId);
      const result = await toggleEnterpriseCoverage(agent.userId, covered, planType);
      if (result.success) {
        toast({ title: "Coverage Updated", description: result.message });
        refetchHierarchy();
        refetchSeatSummary();
      } else {
        toast({ title: "Error", description: result.message, variant: "destructive" });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to toggle coverage",
        variant: "destructive",
      });
    } finally {
      setCoverageLoading(null);
    }
  };

  // Calculate grace period info for each seat
  const seatsWithGraceInfo = useMemo<AgentTableRow[]>(() => {
    return seats.map((seat): AgentTableRow => {
      if (seat.status !== 'grace_period' || !seat.gracePeriodEnd) {
        return seat;
      }

      const now = new Date();
      const endDate = new Date(seat.gracePeriodEnd);
      const daysRemaining = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      const gracePeriodInfo: GracePeriodInfo = {
        isInGracePeriod: true,
        daysRemaining,
        gracePeriodEnd: seat.gracePeriodEnd,
        warningLevel: daysRemaining <= 3 ? 'critical' : daysRemaining <= 7 ? 'warning' : 'info',
      };

      return { ...seat, gracePeriodInfo };
    });
  }, [seats]);

  // Fetch agency seats
  const fetchSeats = async () => {
    if (!agencyId) return;

    try {
      setLoading(true);
      const response = await getAgencySeats(agencyId);
      setSeats(response.seats || []);
      setSummary(response.summary || null);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to fetch agency seats",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSeats();
  }, [agencyId]);

  // Handle promo code validation
  const handleValidatePromoCode = async () => {
    if (!agencyId || !promoCode.trim()) return;

    try {
      setPromoCodeLoading(true);
      setPromoCodeResult(null);
      const result = await validateAgencyPromoCode(agencyId, promoCode.trim());
      setPromoCodeResult(result);

      if (!result.valid) {
        toast({
          title: "Invalid Promo Code",
          description: result.error || "This promo code is not valid",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to validate promo code",
        variant: "destructive",
      });
    } finally {
      setPromoCodeLoading(false);
    }
  };

  // Handle apply coupon
  const handleApplyCoupon = async () => {
    if (!agencyId || !promoCodeResult?.couponId) return;

    try {
      setPromoCodeLoading(true);
      await applyAgencyCoupon(agencyId, promoCodeResult.couponId);

      toast({
        title: "Coupon Applied!",
        description: `Discount of ${promoCodeResult.discountDescription} has been applied to your subscription.`,
      });

      // Clear promo code state
      setPromoCode("");
      setPromoCodeResult(null);

      // Refresh seats to show updated info
      await fetchSeats();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to apply coupon",
        variant: "destructive",
      });
    } finally {
      setPromoCodeLoading(false);
    }
  };

  // Handle payment source toggle
  const handleTogglePaymentSource = async (seat: AgencyBillingSeat, newSource: PaymentSource) => {
    setConfirmDialog({
      open: true,
      type: "toggle_payment",
      seat,
      newValue: newSource,
    });
  };

  // Handle plan change with proration preview
  const handleChangePlan = async (seat: AgencyBillingSeat, newPlan: PlanType) => {
    // DEMO SANDBOX: same reasoning as handleSubscribe — plan changes bill the real
    // Stripe account behind the pool. Blocked server-side too; this makes the click
    // sell instead of erroring.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            'Changing seat plans is disabled in the demo sandbox. Start a free trial to manage billing for your team.',
        }),
      );
      return;
    }

    if (!agencyId) return;

    try {
      setActionLoading(`plan-${seat.userId}`);
      const prorationResponse = await getProrationPreview(agencyId, seat.userId, newPlan);

      setConfirmDialog({
        open: true,
        type: "change_plan",
        seat,
        newValue: newPlan,
        proration: prorationResponse.proration,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to get proration preview",
        variant: "destructive",
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Confirm action
  const handleConfirmAction = async () => {
    if (!confirmDialog.seat || !confirmDialog.type || !agencyId) return;

    try {
      setActionLoading(`confirm-${confirmDialog.seat.userId}`);

      if (confirmDialog.type === "toggle_payment") {
        await togglePaymentSource({
          agencyId,
          userId: confirmDialog.seat.userId,
          newPaymentSource: confirmDialog.seat.paymentSource as PaymentSource,
        });

        const isSwitchingToAgency = confirmDialog.seat.paymentSource === 'self';

        toast({
          title: "Payment Source Updated",
          description: isSwitchingToAgency
            ? `${confirmDialog.seat.userName} is now agency-paid. Their personal subscription has been canceled.`
            : `${confirmDialog.seat.userName} is now self-paid. They have 7 days to add payment.`,
        });
      } else if (confirmDialog.type === "change_plan" && confirmDialog.newValue) {
        await changeAgentPlan({
          agencyId,
          userId: confirmDialog.seat.userId,
          newPlan: confirmDialog.newValue as PlanType,
        });

        toast({
          title: "Plan Changed",
          description: `${confirmDialog.seat.userName}'s plan has been changed to ${confirmDialog.newValue}.`,
        });
      }

      // Refresh data
      await fetchSeats();
      setConfirmDialog({ open: false, type: null, seat: null });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to complete action",
        variant: "destructive",
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Get status badge - Modern pill style with gradient backgrounds
  const getStatusBadge = (status: SeatStatus, gracePeriodInfo?: GracePeriodInfo) => {
    if (status === 'active') {
      return (
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 text-white text-xs font-semibold shadow-sm">
          <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
          Active
        </div>
      );
    }
    if (status === 'grace_period' && gracePeriodInfo) {
      const gradientClass = gracePeriodInfo.warningLevel === 'critical'
        ? 'from-red-500 to-rose-500'
        : gracePeriodInfo.warningLevel === 'warning'
        ? 'from-amber-500 to-orange-500'
        : 'from-blue-500 to-cyan-500';
      return (
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r ${gradientClass} text-white text-xs font-semibold shadow-sm`}>
          <Clock className="h-3 w-3" />
          {gracePeriodInfo.daysRemaining}d left
        </div>
      );
    }
    if (status === 'removed') {
      return (
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-semibold shadow-sm">
          <X className="h-3 w-3" />
          Removed
        </div>
      );
    }
    if (status === 'none') {
      return (
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-gray-400 to-gray-500 text-white text-xs font-semibold shadow-sm">
          <AlertTriangle className="h-3 w-3" />
          No Subscription
        </div>
      );
    }
    return <Badge variant="outline" className="font-medium">{humanizeStatus(status)}</Badge>;
  };

  // Get plan badge - Modern chip style with solid colors
  const getPlanBadge = (planType: PlanType) => {
    if (planType === 'full_fledged') {
      return (
        <div className="inline-flex items-center px-3 py-1 rounded-lg bg-purple-600 text-white text-xs font-bold tracking-wide shadow-sm">
          FULL-FLEDGED
        </div>
      );
    }
    return (
      <div className="inline-flex items-center px-3 py-1 rounded-lg bg-blue-600 text-white text-xs font-bold tracking-wide shadow-sm">
        BASIC
      </div>
    );
  };

  if (!agencyId) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <p className="text-gray-600">You need to be part of an agency to access billing management.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loading && !isEnterpriseOwner) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading agency billing data...</p>
        </div>
      </div>
    );
  }

  // Enterprise owners see ONLY the enterprise coverage management UI
  if (isEnterpriseOwner) {
    return (
      <div className="space-y-6">
        {/* Enterprise Seat Usage */}
        {seatSummary && (
          <Card className="border-purple-200 bg-gradient-to-br from-purple-50/50 to-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-600" />
                Enterprise Seat Coverage
              </CardTitle>
              <CardDescription>
                Manage which agents in your hierarchy are covered by your organization
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Total seats */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">Total Covered Seats</span>
                  <span className="font-semibold">
                    {seatSummary.coveredAgents} / {seatSummary.maxCoveredSeats > 0 ? seatSummary.maxCoveredSeats : '\u221E'}
                  </span>
                </div>
                {seatSummary.maxCoveredSeats > 0 && (
                  <Progress
                    value={(seatSummary.coveredAgents / seatSummary.maxCoveredSeats) * 100}
                    className="h-2"
                  />
                )}
              </div>

              {/* Per-plan breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-white rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">Total Agents</div>
                  <div className="text-xl font-bold">{seatSummary.totalAgents}</div>
                  <div className="text-xs text-muted-foreground">{seatSummary.selfPayAgents} self-pay</div>
                </div>
                <div className="bg-white rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">Basic Seats</div>
                  <div className="text-xl font-bold text-blue-600">
                    {seatSummary.coveredBasicSeats} / {seatSummary.maxBasicSeats > 0 ? seatSummary.maxBasicSeats : '\u221E'}
                  </div>
                  {seatSummary.maxBasicSeats > 0 && (
                    <Progress value={(seatSummary.coveredBasicSeats / seatSummary.maxBasicSeats) * 100} className="h-1.5 mt-1" />
                  )}
                </div>
                <div className="bg-white rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground">Full-Fledged Seats</div>
                  <div className="text-xl font-bold text-purple-600">
                    {seatSummary.coveredFullFledgedSeats} / {seatSummary.maxFullFledgedSeats > 0 ? seatSummary.maxFullFledgedSeats : '\u221E'}
                  </div>
                  {seatSummary.maxFullFledgedSeats > 0 && (
                    <Progress value={(seatSummary.coveredFullFledgedSeats / seatSummary.maxFullFledgedSeats) * 100} className="h-1.5 mt-1" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Enterprise Hierarchy Agents Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Agent Coverage Management
            </CardTitle>
            <CardDescription>
              Toggle coverage for agents across your organization hierarchy
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!hierarchyAgents ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                <span className="ml-3 text-muted-foreground">Loading agents...</span>
              </div>
            ) : hierarchyAgents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No agents found in your organization hierarchy.
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agent</TableHead>
                      <TableHead>Agency</TableHead>
                      <TableHead>Account Type</TableHead>
                      <TableHead>Coverage</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {hierarchyAgents.map((agent, index) => (
                      <TableRow key={`${agent.userId}-${agent.agencyId}-${index}`}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{agent.name}</div>
                            <div className="text-sm text-muted-foreground">{agent.email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{agent.agencyName}</span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {agent.accountType?.replace('_', ' ') || 'N/A'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {agent.isCovered ? (
                            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-100 text-green-800 text-xs font-semibold">
                              <Check className="h-3 w-3" />
                              Covered ({agent.planType?.replace('_', ' ') || 'basic'})
                            </div>
                          ) : (
                            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
                              Self-pay
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {agent.role === 'owner' ? (
                            <span className="text-xs text-muted-foreground">Owner</span>
                          ) : coverageLoading === agent.userId ? (
                            <div className="flex items-center gap-2">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span className="text-xs text-muted-foreground">Updating...</span>
                            </div>
                          ) : (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" className="text-xs">
                                  {agent.isCovered ? 'Change Plan' : 'Cover Agent'}
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                <DropdownMenuItem onClick={() => handleToggleCoverage(agent, true, 'basic')}>
                                  {agent.isCovered && agent.planType === 'basic' ? '✓ ' : ''}Basic
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleToggleCoverage(agent, true, 'full_fledged')}>
                                  {agent.isCovered && agent.planType === 'full_fledged' ? '✓ ' : ''}Full-Fledged
                                </DropdownMenuItem>
                                {agent.isCovered && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="text-red-600"
                                      onClick={() => handleToggleCoverage(agent, false, agent.planType as 'lite' | 'basic' | 'full_fledged' || 'basic')}
                                    >
                                      Remove Coverage
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Regular (non-enterprise) agency billing below
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading agency billing data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enterprise Seat Usage (enterprise owners only) */}
      {isEnterpriseOwner && seatSummary && (
        <Card className="border-purple-200 bg-gradient-to-br from-purple-50/50 to-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-600" />
              Enterprise Seat Coverage
            </CardTitle>
            <CardDescription>
              Manage which agents in your hierarchy are covered by your organization
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Total seats */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="font-medium">Total Covered Seats</span>
                <span className="font-semibold">
                  {seatSummary.coveredAgents} / {seatSummary.maxCoveredSeats > 0 ? seatSummary.maxCoveredSeats : '∞'}
                </span>
              </div>
              {seatSummary.maxCoveredSeats > 0 && (
                <Progress
                  value={(seatSummary.coveredAgents / seatSummary.maxCoveredSeats) * 100}
                  className="h-2"
                />
              )}
            </div>

            {/* Per-plan breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-white rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Total Agents</div>
                <div className="text-xl font-bold">{seatSummary.totalAgents}</div>
                <div className="text-xs text-muted-foreground">{seatSummary.selfPayAgents} self-pay</div>
              </div>
              <div className="bg-white rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Basic Seats</div>
                <div className="text-xl font-bold text-blue-600">
                  {seatSummary.coveredBasicSeats} / {seatSummary.maxBasicSeats > 0 ? seatSummary.maxBasicSeats : '∞'}
                </div>
                {seatSummary.maxBasicSeats > 0 && (
                  <Progress value={(seatSummary.coveredBasicSeats / seatSummary.maxBasicSeats) * 100} className="h-1.5 mt-1" />
                )}
              </div>
              <div className="bg-white rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Full-Fledged Seats</div>
                <div className="text-xl font-bold text-purple-600">
                  {seatSummary.coveredFullFledgedSeats} / {seatSummary.maxFullFledgedSeats > 0 ? seatSummary.maxFullFledgedSeats : '∞'}
                </div>
                {seatSummary.maxFullFledgedSeats > 0 && (
                  <Progress value={(seatSummary.coveredFullFledgedSeats / seatSummary.maxFullFledgedSeats) * 100} className="h-1.5 mt-1" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Enterprise Hierarchy Agents Table (enterprise owners only) */}
      {isEnterpriseOwner && hierarchyAgents && hierarchyAgents.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Agent Coverage Management
            </CardTitle>
            <CardDescription>
              Toggle coverage for agents across your organization hierarchy
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agent</TableHead>
                    <TableHead>Agency</TableHead>
                    <TableHead>Account Type</TableHead>
                    <TableHead>Coverage</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hierarchyAgents.map((agent) => (
                    <TableRow key={agent.userId}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{agent.name}</div>
                          <div className="text-sm text-muted-foreground">{agent.email}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{agent.agencyName}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {agent.accountType?.replace('_', ' ') || 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {agent.isCovered ? (
                          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-100 text-green-800 text-xs font-semibold">
                            <Check className="h-3 w-3" />
                            Covered ({agent.planType?.replace('_', ' ') || 'basic'})
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
                            Self-pay
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {agent.role === 'owner' ? (
                          <span className="text-xs text-muted-foreground">Owner</span>
                        ) : coverageLoading === agent.userId ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : agent.isCovered ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleCoverage(agent, false, 'basic')}
                            className="text-xs"
                          >
                            <X className="h-3 w-3 mr-1" />
                            Remove Coverage
                          </Button>
                        ) : (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm" className="text-xs">
                                <Check className="h-3 w-3 mr-1" />
                                Cover Agent
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuItem onClick={() => handleToggleCoverage(agent, true, 'basic')}>
                                Cover as Basic
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleToggleCoverage(agent, true, 'full_fledged')}>
                                Cover as Full-Fledged
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Agents</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.totalAgents || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {summary?.activeSeats || 0} active, {summary?.gracePeriodSeats || 0} grace period
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Agency-Paid</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.agencyPaidAgents || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {summary?.selfPaidAgents || 0} self-paid agents
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Agency Monthly Cost</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${summary?.agencyMonthlyCost?.toFixed(2) || '0.00'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Self-paid: ${summary?.selfPaidMonthlyCost?.toFixed(2) || '0.00'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Promo Code Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Apply Discount Code
          </CardTitle>
          <CardDescription>
            Have a promo code? Enter it below to apply a discount to your agency subscription.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Input
                placeholder="Enter promo code"
                value={promoCode}
                onChange={(e) => {
                  setPromoCode(e.target.value.toUpperCase());
                  setPromoCodeResult(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && promoCode.trim()) {
                    handleValidatePromoCode();
                  }
                }}
                disabled={promoCodeLoading}
                className="uppercase"
              />
            </div>
            {!promoCodeResult?.valid ? (
              <Button
                onClick={handleValidatePromoCode}
                disabled={!promoCode.trim() || promoCodeLoading}
                className="min-w-[120px]"
              >
                {promoCodeLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Validating...
                  </>
                ) : (
                  "Validate"
                )}
              </Button>
            ) : (
              <Button
                onClick={handleApplyCoupon}
                disabled={promoCodeLoading}
                className="min-w-[120px] bg-green-600 hover:bg-green-700"
              >
                {promoCodeLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Applying...
                  </>
                ) : (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Apply Discount
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Show validation result */}
          {promoCodeResult?.valid && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-green-800">Valid Promo Code!</p>
                  <p className="text-sm text-green-700 mt-1">
                    <span className="font-medium">{promoCodeResult.discountDescription}</span>
                    {promoCodeResult.name && (
                      <span className="ml-2 text-green-600">({promoCodeResult.name})</span>
                    )}
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    Click "Apply Discount" to add this to your subscription.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Grace Period Warnings */}
      {seatsWithGraceInfo.some(s => s.gracePeriodInfo?.warningLevel === 'critical') && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-800">
              <AlertTriangle className="h-5 w-5" />
              Critical: Agents Expiring Soon
            </CardTitle>
            <CardDescription className="text-red-700">
              The following agents have less than 3 days remaining in their grace period:
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {seatsWithGraceInfo
                .filter(s => s.gracePeriodInfo?.warningLevel === 'critical')
                .map(seat => (
                  <li key={seat.userId} className="flex items-center justify-between">
                    <span className="font-medium">{seat.userName}</span>
                    <Badge className="bg-red-600 text-white">
                      {seat.gracePeriodInfo?.daysRemaining} days left
                    </Badge>
                  </li>
                ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Agents Table */}
      <Card>
        <CardHeader>
          <CardTitle>Agent Billing Management</CardTitle>
          <CardDescription>
            Manage subscriptions and payment sources for all agents in your agency
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Agent</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Payment Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Monthly Cost</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {seatsWithGraceInfo.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No agents found. Add agents to your agency to manage their billing.
                    </TableCell>
                  </TableRow>
                ) : (
                  seatsWithGraceInfo.map((seat) => (
                    <TableRow key={seat.userId}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{seat.userName}</div>
                          <div className="text-sm text-muted-foreground">{seat.userEmail}</div>
                        </div>
                      </TableCell>
                      <TableCell>{getPlanBadge(seat.planType)}</TableCell>
                      <TableCell>
                        {seat.paymentSource === PaymentSource.AGENCY ? (
                          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-semibold shadow-sm">
                            <DollarSign className="h-3 w-3" />
                            Agency Paid
                          </div>
                        ) : seat.paymentSource === PaymentSource.SELF ? (
                          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-gradient-to-r from-slate-500 to-gray-600 text-white text-xs font-semibold shadow-sm">
                            <DollarSign className="h-3 w-3" />
                            Self Paid
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-gradient-to-r from-gray-300 to-gray-400 text-gray-700 text-xs font-semibold shadow-sm">
                            <AlertTriangle className="h-3 w-3" />
                            No Payment
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(seat.status, seat.gracePeriodInfo)}
                      </TableCell>
                      <TableCell>${seat.monthlyPrice.toFixed(2)}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" disabled={actionLoading !== null}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuLabel>Quick Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator />

                            {/* Payment Source Toggle */}
                            <DropdownMenuItem
                              onClick={() =>
                                handleTogglePaymentSource(
                                  seat,
                                  seat.paymentSource === PaymentSource.AGENCY ? PaymentSource.SELF : PaymentSource.AGENCY
                                )
                              }
                            >
                              <DollarSign className="mr-2 h-4 w-4" />
                              Switch to {seat.paymentSource === PaymentSource.AGENCY ? 'Self-Paid' : 'Agency-Paid'}
                            </DropdownMenuItem>

                            <DropdownMenuSeparator />
                            <DropdownMenuLabel>Change Plan</DropdownMenuLabel>

                            {/* Upgrade/Downgrade Options */}
                            {seat.planType === PlanType.BASIC ? (
                              <DropdownMenuItem
                                onClick={() => handleChangePlan(seat, PlanType.FULL_FLEDGED)}
                              >
                                <ArrowUpCircle className="mr-2 h-4 w-4 text-green-600" />
                                Upgrade to Full-Fledged
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onClick={() => handleChangePlan(seat, PlanType.BASIC)}
                              >
                                <ArrowDownCircle className="mr-2 h-4 w-4 text-orange-600" />
                                Downgrade to Basic
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, type: null, seat: null })}>
        <ResponsiveDialogContent size="lg">
          <DialogHeader>
            <DialogTitle>
              {confirmDialog.type === "toggle_payment"
                ? "Change Payment Source"
                : "Change Subscription Plan"}
            </DialogTitle>
            <DialogDescription>
              {confirmDialog.type === "toggle_payment" && confirmDialog.seat && (
                <div className="space-y-3 pt-4">
                  <p className="text-base">
                    Change payment source for <strong>{confirmDialog.seat.userName}</strong>?
                  </p>

                  {confirmDialog.seat.paymentSource === 'self' ? (
                    // Self → Agency
                    <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg space-y-2">
                      <div className="flex items-start gap-2">
                        <DollarSign className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div className="space-y-2 flex-1">
                          <p className="font-medium text-blue-900">Switching to Agency-Paid</p>
                          <ul className="text-sm text-blue-700 space-y-1.5 list-disc list-inside">
                            <li>Agent's personal subscription will be <strong>canceled immediately</strong></li>
                            <li>Agency will be charged <strong>${confirmDialog.seat.planType === 'full_fledged' ? '240' : '120'}/month</strong></li>
                            <li>Plan type: <strong>{confirmDialog.seat.planType === 'full_fledged' ? 'Full-Fledged' : 'Basic'}</strong> (same as current)</li>
                            <li>Proration will be applied for the current billing period</li>
                            <li>Agent will receive an email notification</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Agency → Self
                    <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg space-y-2">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="space-y-2 flex-1">
                          <p className="font-medium text-amber-900">Switching to Self-Paid</p>
                          <ul className="text-sm text-amber-700 space-y-1.5 list-disc list-inside">
                            <li>Agency seat will be <strong>removed immediately</strong></li>
                            <li>Agent enters a <strong>7-day grace period</strong></li>
                            <li>Agent must add payment method within 7 days to maintain access</li>
                            <li>After 7 days without payment, access will be <strong>suspended</strong></li>
                            <li>Agent will receive an email with instructions</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {confirmDialog.type === "change_plan" && confirmDialog.seat && confirmDialog.proration && (
                <div className="space-y-3 pt-4">
                  <p>
                    Change <strong>{confirmDialog.seat.userName}</strong>'s plan to{" "}
                    <strong>{confirmDialog.newValue === 'full_fledged' ? 'Full-Fledged' : 'Basic'}</strong>?
                  </p>
                  <div className="bg-gray-50 p-4 rounded space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Current charge:</span>
                      <span className="font-medium">${confirmDialog.proration.currentCharge.toFixed(2)}/mo</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>New charge:</span>
                      <span className="font-medium">${confirmDialog.proration.newCharge.toFixed(2)}/mo</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between font-semibold">
                      <span>{confirmDialog.proration.isUpgrade ? 'Prorated charge today:' : 'Prorated credit:'}</span>
                      <span className={confirmDialog.proration.isUpgrade ? 'text-red-600' : 'text-green-600'}>
                        {confirmDialog.proration.isUpgrade ? '+' : '-'}${Math.abs(confirmDialog.proration.prorationAmount).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {confirmDialog.proration.description}
                  </p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialog({ open: false, type: null, seat: null })}
              disabled={actionLoading !== null}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAction}
              disabled={actionLoading !== null}
            >
              {actionLoading ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>
    </div>
  );
};
